import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import WarehouseForm from '../../components/Configuration/WarehouseForm';
import { getWarehouses, deleteWarehouse } from '../../services/warehousesService';

const Warehouses = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const [allWarehouses, setAllWarehouses] = useState([]);

  // Cargar almacenes desde el backend (solo una vez)
  const loadWarehouses = async () => {
    try {
      setLoading(true);
      console.log('🔄 [Warehouses] Iniciando carga de almacenes...');
      
      const response = await getWarehouses();
      
      console.log('📥 [Warehouses] Respuesta recibida en componente:');
      console.log('📦 [Warehouses] response:', response);
      console.log('✅ [Warehouses] response.success:', response.success);
      console.log('📋 [Warehouses] response.data:', response.data);
      console.log('🔍 [Warehouses] Tipo de response.data:', typeof response.data);
      console.log('📊 [Warehouses] Es array?:', Array.isArray(response.data));
      
      if (response.success) {
        const warehousesData = response.data || [];
        console.log('💾 [Warehouses] Guardando almacenes en estado:');
        console.log('🔢 [Warehouses] Cantidad de almacenes a guardar:', warehousesData.length);
        console.log('📝 [Warehouses] Primeros 3 almacenes:', warehousesData.slice(0, 3));
        
        if (warehousesData.length > 0) {
          console.log('🔑 [Warehouses] Estructura del primer almacén:', {
            id: warehousesData[0].id,
            code: warehousesData[0].code,
            name: warehousesData[0].name,
            is_active: warehousesData[0].is_active,
            keys: Object.keys(warehousesData[0])
          });
        }
        
        setAllWarehouses(warehousesData);
        console.log('✅ [Warehouses] Almacenes guardados en estado exitosamente');
      } else {
        console.warn('⚠️ [Warehouses] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los almacenes',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Warehouses] Error al cargar almacenes:', error);
      console.error('📊 [Warehouses] Error.response:', error.response);
      console.error('📋 [Warehouses] Error.response?.data:', error.response?.data);
      console.error('🔢 [Warehouses] Error.status:', error.response?.status);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los almacenes',
        type: 'error'
      });
    } finally {
      setLoading(false);
      console.log('🏁 [Warehouses] Proceso de carga finalizado');
    }
  };

  useEffect(() => {
    // Evitar doble carga en React StrictMode - Carga inicial única
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadWarehouses();
    }
  }, []);

  // Filtrar almacenes localmente en el frontend
  const filteredWarehouses = allWarehouses.filter(warehouse => {
    const matchesSearch = !searchTerm || 
      warehouse.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && warehouse.is_active) ||
      (statusFilter === 'inactive' && !warehouse.is_active);

    return matchesSearch && matchesStatus;
  });

  // Calcular estadísticas
  const totalWarehouses = filteredWarehouses.length;
  const activeWarehouses = filteredWarehouses.filter(w => w.is_active).length;
  const inactiveWarehouses = filteredWarehouses.filter(w => !w.is_active).length;

  const handleDeleteClick = (warehouse) => {
    setWarehouseToDelete(warehouse);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!warehouseToDelete) return;
    
    try {
      const response = await deleteWarehouse(warehouseToDelete.id);
      
      if (response.success) {
        setAllWarehouses(prev => prev.filter(w => w.id !== warehouseToDelete.id));
        setToast({
          isOpen: true,
          message: 'Almacén eliminado exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el almacén',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar almacén:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el almacén',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setWarehouseToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    // Recargar todos los almacenes desde el backend
    loadWarehouses();
    setToast({
      isOpen: true,
      message: editingWarehouse ? 'Almacén actualizado exitosamente' : 'Almacén creado exitosamente',
      type: 'success'
    });
  };


  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Almacenes</h1>
              <p className="text-gray-600 mt-1">Gestione los almacenes de la empresa</p>
            </div>
            <button
              onClick={() => {
                setEditingWarehouse(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Almacén
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Almacenes</span>
          </nav>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex gap-2">
              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>

              {/* Búsqueda */}
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Almacenes</div>
            <div className="text-2xl font-bold text-gray-800">{totalWarehouses}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Almacenes Activos</div>
            <div className="text-2xl font-bold text-green-600">{activeWarehouses}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Almacenes Inactivos</div>
            <div className="text-2xl font-bold text-red-600">{inactiveWarehouses}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando almacenes...
            </div>
          ) : filteredWarehouses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? 'No se encontraron almacenes con los filtros aplicados' 
                : 'No hay almacenes registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWarehouses.map((warehouse) => (
                    <tr key={warehouse.id} className="hover:bg-gray-50">
                      {/* Código */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-teal-600">{warehouse.code}</span>
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{warehouse.name}</div>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          warehouse.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {warehouse.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingWarehouse(warehouse);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(warehouse)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario */}
        <WarehouseForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingWarehouse(null);
          }}
          warehouse={editingWarehouse}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setWarehouseToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Almacén"
          message={`¿Estás seguro de que deseas eliminar el almacén "${warehouseToDelete?.name}" (${warehouseToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Warehouses;

